import React, { useState, useEffect } from "react";
import { goToCheckout } from "@/lib/checkout";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AuthShell from "@/components/AuthShell";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { CountrySelect } from "@/components/CountrySelect";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { COUNTRIES } from "@/lib/countries";
import { PARTNER_CATEGORY_KEYS, partnerCategoryLabel, guessCategory } from "@/lib/partnerCategories";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { buildPlans, regionForCountry } from "@/lib/plans";
import { PlanRecapCard } from "@/components/PlanRecapCard";
import { MethodChoice } from "@/components/PaymentMethodGate";
import { api } from "@/lib/api";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { RecaptchaNotice } from "@/components/RecaptchaNotice";
import { FindoSpinner } from "@/components/FindoSpinner";
import { SignupPlanDialog } from "@/components/SignupPlanDialog";
import { GOOGLE_SIGNUP_KEY } from "@/components/GoogleAuthWatcher";

const readGoogleSignup = () => { try { return JSON.parse(sessionStorage.getItem(GOOGLE_SIGNUP_KEY) || "null"); } catch { return null; } };
const lsGet = (k, d = null) => { try { return localStorage.getItem(k) || d; } catch { return d; } };

export default function Register() {
  const { register } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    org_name: "", first_name: "", email: "", password: "", confirm: "", country: "FR", city: "", category: "", siret: "", vat_number: "", referral_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [cgu, setCgu] = useState(false);
  const [categoryTouched, setCategoryTouched] = useState(false);
  const [recap, setRecap] = useState(null);
  const [payMethod, setPayMethod] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  // Google signup: verified identity (no password), then explicit plan step.
  const [google, setGoogle] = useState(() => (params.get("google") ? readGoogleSignup() : null));
  const [planStep, setPlanStep] = useState(false);

  useEffect(() => {
    if (google) setForm((f) => ({ ...f, email: google.email, first_name: f.first_name || (google.name || "").split(" ")[0] }));
  }, [google]);

  function cancelGoogle() {
    sessionStorage.removeItem(GOOGLE_SIGNUP_KEY);
    setGoogle(null);
    setForm((f) => ({ ...f, email: "" }));
    navigate("/auth/register", { replace: true });
  }

  // Auto-guess the category from the establishment name until the user picks one.
  function onOrgChange(e) {
    const org_name = e.target.value;
    setForm((f) => {
      const next = { ...f, org_name };
      if (!categoryTouched) {
        const g = guessCategory(org_name);
        if (g) next.category = g;
      }
      return next;
    });
  }

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref }));
    // Resume link from the abandoned-checkout reminder email.
    const plan = params.get("plan");
    if (plan) {
      try {
        localStorage.setItem("findo_plan_tier", plan);
        if (params.get("cycle")) localStorage.setItem("findo_plan_cycle", params.get("cycle"));
        if (params.get("region")) localStorage.setItem("findo_plan_region", params.get("region"));
      } catch { /* ignore */ }
    }
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  async function submit(e) {
    e.preventDefault();
    if (!google && form.password !== form.confirm) {
      toast.error(t("a.passwordMismatch"));
      return;
    }
    if (form.country === "FR" && (!form.siret.trim() || !form.vat_number.trim())) {
      toast.error(t("a.legalRequiredFR"));
      return;
    }
    if (!cgu) {
      toast.error(t("sub.cguRequired"));
      return;
    }
    if (google) { setPlanStep(true); return; }
    await continueWithPlan(lsGet("findo_plan_tier"), lsGet("findo_plan_cycle", "monthly"));
  }

  const signupRegion = () => (lsGet("findo_plan_region") || regionForCountry(form.country)).toLowerCase();

  async function continueWithPlan(planTier, cycle) {
    if (planTier && planTier !== "free") {
      // Show a short recap of the plan + price + payment method BEFORE redirecting to Stripe.
      const region = signupRegion();
      const plan = buildPlans(region, lang).find((p) => p.key === planTier);
      setRecap({ tier: planTier, cycle, region, annual: cycle === "annual", name: plan?.name || planTier, price: plan?.price_eur || 0 });
      return;
    }

    // FREE plan: create the account immediately.
    setLoading(true);
    try {
      await register(
        form.email.trim().toLowerCase(), google ? undefined : form.password, form.org_name.trim(),
        form.country, form.referral_code.trim() || undefined, await getRecaptchaToken("register"),
        form.city.trim() || undefined, form.category || undefined,
        form.siret.trim() || undefined, form.vat_number.trim() || undefined, true,
        form.first_name.trim() || undefined, google?.google_token
      );
      sessionStorage.removeItem(GOOGLE_SIGNUP_KEY);
      toast.success(t("hc.accountCreated"));
      navigate("/b2b/registry");
    } catch (err) {
      toast.error(err.message || t("hc.signupFailed"));
    } finally {
      setLoading(false);
    }
  }

  async function doPaidCheckout() {
    if (!recap || !payMethod) return;
    setRedirecting(true);
    try {
      const captcha_token = await getRecaptchaToken("register");
      const res = await api("/auth/register-checkout", {
        method: "POST",
        auth: false,
        body: {
          email: form.email.trim().toLowerCase(), password: google ? undefined : form.password, google_token: google?.google_token,
          org_name: form.org_name.trim(),
          first_name: form.first_name.trim() || undefined, country: form.country,
          city: form.city.trim() || undefined, category: form.category || undefined,
          siret: form.siret.trim() || undefined, vat_number: form.vat_number.trim() || undefined,
          referral_code: form.referral_code.trim() || undefined, cgu_accepted: true,
          tier: recap.tier, billing_cycle: recap.cycle, region: recap.region, origin_url: window.location.origin,
          captcha_token, payment_method: payMethod,
        },
      });
      toast.success(t("hc.redirectingToPayment"));
      goToCheckout(res.url);
    } catch (err) {
      setRedirecting(false);
      setRecap(null);
      toast.error(err.message || t("hc.cannotOpenPayment"));
    }
  }

  return (
    <AuthShell title={t("a.registerTitle")} subtitle={t("a.registerSub")}>
      {google && (
        <div className="mb-4 rounded-xl border border-findo-indigo/20 bg-findo-indigo/5 px-4 py-3 text-sm" data-testid="register-google-banner">
          <p className="font-semibold text-findo-navy">{t("gs.connected").replace("{email}", google.email)}</p>
          <p className="text-slate-600 mt-0.5">{t("gs.complete")}</p>
          <button type="button" onClick={cancelGoogle} className="mt-1 text-xs font-semibold text-findo-indigo underline" data-testid="register-google-cancel">{t("gs.cancel")}</button>
        </div>
      )}
      <form onSubmit={submit} className="findo-form-fields space-y-3.5" data-testid="register-form">
        <div>
          <Label htmlFor="org">{t("a.org")}</Label>
          <Input id="org" required value={form.org_name} onChange={onOrgChange}
            placeholder={t("hc.hotelBelmont")} className="mt-1.5 h-11 rounded-xl" data-testid="register-org" />
        </div>
        <div>
          <Label htmlFor="first_name">{t("a.firstName")}</Label>
          <Input id="first_name" value={form.first_name} onChange={set("first_name")}
            placeholder="Camille" className="mt-1.5 h-11 rounded-xl" data-testid="register-first-name" />
        </div>
        <div>
          <Label htmlFor="email">{t("a.emailPro")}</Label>
          <Input id="email" type="email" required value={form.email} onChange={set("email")} readOnly={!!google}
            placeholder="contact@hotel.com" className="mt-1.5 h-11 rounded-xl" data-testid="register-email" />
        </div>
        {!google && <>
        <div>
          <Label htmlFor="pwd">{t("a.password")}</Label>
          <PasswordInput id="pwd" required minLength={6} value={form.password} onChange={set("password")}
            placeholder={t("a.min6")} className="mt-1.5 h-11 rounded-xl" data-testid="register-password" />
        </div>
        <div>
          <Label htmlFor="pwd2">{t("a.confirmPassword")}</Label>
          <PasswordInput id="pwd2" required minLength={6} value={form.confirm} onChange={set("confirm")}
            placeholder={t("a.min6")} className="mt-1.5 h-11 rounded-xl" data-testid="register-confirm" />
          {form.confirm && form.password !== form.confirm && (
            <p className="text-xs text-findo-red mt-1" data-testid="register-mismatch">{t("a.passwordMismatch")}</p>
          )}
        </div>
        </>}
        <div>
          <Label>{t("a.country")}</Label>
          <div className="mt-1.5">
            <CountrySelect value={form.country} onChange={set("country")} testid="register-country" />
          </div>
        </div>
        <div>
          <Label htmlFor="city">{t("a.city")}</Label>
          <div className="mt-1.5">
            <CityAutocomplete value={form.city} onChange={(v) => setForm((f) => ({ ...f, city: v }))}
              country={form.country} placeholder="Paris" className="h-11 rounded-xl" testid="register-city" />
          </div>
        </div>
        <div>
          <Label htmlFor="category">{t("a.category")}</Label>
          <div className="mt-1.5">
            <Select value={form.category} onValueChange={(v) => { setCategoryTouched(true); setForm((f) => ({ ...f, category: v })); }}>
              <SelectTrigger className="h-11 rounded-full" data-testid="register-category">
                <SelectValue placeholder={t("a.categoryPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {PARTNER_CATEGORY_KEYS.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`register-category-${k}`}>{partnerCategoryLabel(t, k)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="siret">{t("a.siret")}{form.country === "FR" && <span className="text-findo-red"> *</span>}</Label>
            <Input id="siret" value={form.siret} onChange={set("siret")} required={form.country === "FR"}
              placeholder="123 456 789 00011" className="mt-1.5 h-11 rounded-xl" data-testid="register-siret" />
          </div>
          <div>
            <Label htmlFor="vat">{t("a.vat")}{form.country === "FR" && <span className="text-findo-red"> *</span>}</Label>
            <Input id="vat" value={form.vat_number} onChange={set("vat_number")} required={form.country === "FR"}
              placeholder="FR12345678900" className="mt-1.5 h-11 rounded-xl uppercase" data-testid="register-vat" />
          </div>
        </div>
        {form.country === "FR" && (
          <p className="text-xs text-slate-400 -mt-1" data-testid="register-legal-hint">{t("a.legalHint")}</p>
        )}
        <div>
          <Label htmlFor="ref">{t("a.referral")}</Label>
          <div className="mt-1.5 flex gap-2">
            <Input id="ref"
              value={form.referral_code ? `https://findloop.io/register?ref=${form.referral_code}` : ""}
              onChange={(e) => {
                const v = e.target.value;
                const raw = v.includes("ref=") ? v.split("ref=").pop() : v;
                const code = (raw || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 8);
                setForm((f) => ({ ...f, referral_code: code }));
              }}
              placeholder="https://findloop.io/register?ref=ABCD1234"
              className="h-11 rounded-xl" data-testid="register-referral" />
            <Button type="button" variant="outline"
              disabled={!form.referral_code}
              onClick={() => { navigator.clipboard.writeText(form.referral_code); toast.success(`${t("hc.codeCopied")} : ${form.referral_code}`); }}
              className="h-11 rounded-xl shrink-0 px-4 font-semibold" data-testid="register-referral-copy">
              {t("hc.copy")}
            </Button>
          </div>
        </div>
        <label className="flex items-start gap-3 rounded-xl border border-border px-3 py-2.5 cursor-pointer" data-testid="register-cgu-label">
          <input type="checkbox" checked={cgu} onChange={(e) => setCgu(e.target.checked)} className="mt-0.5 accent-findo-indigo h-4 w-4" data-testid="register-cgu-checkbox" />
          <span className="text-sm text-slate-600">
            {t("sub.cguPre")} <a href="/cgu" target="_blank" rel="noopener noreferrer" className="text-findo-indigo font-semibold underline">{t("sub.cguLink")}</a> {t("sub.cguAnd")} <a href="/confidentialite" target="_blank" rel="noopener noreferrer" className="text-findo-indigo font-semibold underline">{t("sub.privacyLink")}</a> {t("sub.cguSuffix")}
          </span>
        </label>
        <Button type="submit" disabled={loading || !cgu} className="w-full h-11 rounded-xl font-semibold" data-testid="register-submit">
          {loading ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("a.creating")}</span> : t("a.createBtn")}
        </Button>
      </form>
      <RecaptchaNotice className="mt-4 text-center" />

      {!google && <>
        <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
          <span className="flex-1 h-px bg-border" /> {t("a.or")} <span className="flex-1 h-px bg-border" />
        </div>
        <GoogleAuthButton label={t("a.googleSignup")} redirectPath="/auth/register" />
      </>}

      {planStep && (
        <SignupPlanDialog open region={signupRegion()} initialTier={lsGet("findo_plan_tier")} initialCycle={lsGet("findo_plan_cycle")}
          onClose={() => setPlanStep(false)}
          onPick={({ tier, cycle }) => { setPlanStep(false); continueWithPlan(tier, cycle); }} />
      )}

      <p className="mt-6 text-center text-sm text-slate-500">
        {t("a.haveAccount")}{" "}
        <Link to="/auth/login" className="font-semibold text-findo-indigo hover:underline">{t("a.signin")}</Link>
      </p>

      <Dialog open={!!recap} onOpenChange={(o) => { if (!o && !redirecting) setRecap(null); }}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="checkout-recap-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{t("hc.confirmYourSubscription")}</DialogTitle>
            <DialogDescription>{t("hc.youWillBeRedirectedTo")}</DialogDescription>
          </DialogHeader>
          {recap && <PlanRecapCard name={recap.name} price={recap.price} annual={recap.annual} />}
          <MethodChoice value={payMethod} onChange={setPayMethod} testid="register-pm" />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" className="rounded-xl font-semibold" disabled={redirecting}
              onClick={() => setRecap(null)} data-testid="checkout-recap-cancel">{t("hc.edit")}</Button>
            <Button type="button" className="rounded-xl font-semibold" disabled={redirecting || !payMethod}
              onClick={doPaidCheckout} data-testid="checkout-recap-confirm">
              {redirecting ? t("hc.redirecting") : t("hc.confirmAndPay")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AuthShell>
  );
}
